import type { ChainBranding } from "../config/tenant";

// ---------------------------------------------------------------------------
// Last branding payload (for CSS variables + optional page backgrounds)
// ---------------------------------------------------------------------------

let lastChainBranding: ChainBranding | null = null;

// ---------------------------------------------------------------------------
// Color types -- interface stays the same so existing components work as-is
// ---------------------------------------------------------------------------

export interface BrandColorSet {
	primary: string;
	currentStep?: string;
	nextStep?: string;
	footerBtn?: string;
	footerBtnDisabled?: string;
	genderToggleBtn?: string;
	guestPlaceholderBanner?: string;
	physicalKey?: string;
	physicalKeyText?: string;
	accent: string;
	lightAccent: string;
}

interface BrandTexts {
	newsletterCheckboxBrand: string;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

const DEFAULT_PRIMARY = "#54525D";
const DEFAULT_ACCENT = "#F47847";

const defaultColors: BrandColorSet = {
	primary: DEFAULT_PRIMARY,
	accent: DEFAULT_ACCENT,
	currentStep: DEFAULT_PRIMARY,
	nextStep: DEFAULT_ACCENT,
	footerBtn: "#E3DAD1",
	footerBtnDisabled: DEFAULT_ACCENT,
	genderToggleBtn: DEFAULT_ACCENT,
	physicalKey: DEFAULT_PRIMARY,
	physicalKeyText: "#FFFFFF",
	guestPlaceholderBanner: "rgba(124, 98, 75, 0.15)",
	lightAccent: "#1E2844",
};

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let brandColorsLight: BrandColorSet = { ...defaultColors };
let brandColorsDark: BrandColorSet | null = null;
let prefersDark = false;

let brandLogoUrl = "";
let brandLogoDarkUrl = "";
let brandTexts: BrandTexts = { newsletterCheckboxBrand: "" };

// ---------------------------------------------------------------------------
// User theme preference (when hotel has dark branding)
// ---------------------------------------------------------------------------

export type ThemePreference = "system" | "light" | "dark";

const THEME_PREF_STORAGE_KEY = "oc-theme-preference";

let userThemePreference: ThemePreference = "system";
let themeEpoch = 0;
const themeListeners = new Set<() => void>();

function loadThemePreferenceFromStorage(): ThemePreference {
	if (typeof window === "undefined") return "system";
	try {
		const raw = localStorage.getItem(THEME_PREF_STORAGE_KEY);
		if (raw === "light" || raw === "dark" || raw === "system") return raw;
	} catch {
		/* ignore */
	}
	return "system";
}

function persistThemePreference(p: ThemePreference): void {
	userThemePreference = p;
	try {
		localStorage.setItem(THEME_PREF_STORAGE_KEY, p);
	} catch {
		/* ignore */
	}
}

function bumpThemeEpoch(): void {
	themeEpoch++;
	themeListeners.forEach((l) => l());
}

export function subscribeBrandingTheme(onStoreChange: () => void): () => void {
	themeListeners.add(onStoreChange);
	return () => themeListeners.delete(onStoreChange);
}

export function getBrandingThemeEpoch(): number {
	return themeEpoch;
}

function computeEffectiveDark(): boolean {
	if (!brandColorsDark) return false;
	if (userThemePreference === "light") return false;
	if (userThemePreference === "dark") return true;
	return prefersDark;
}

/** True when the hotel configured dark colors + darkBrandingEnabled (chain-info). */
export function isDarkBrandingAvailable(): boolean {
	return brandColorsDark !== null;
}

export function getThemePreference(): ThemePreference {
	return userThemePreference;
}

export function setThemePreference(p: ThemePreference): void {
	if (!brandColorsDark && (p === "light" || p === "dark")) {
		p = "system";
	}
	persistThemePreference(p);
	refreshRootThemeAttribute();
	bumpThemeEpoch();
}

/** Switch to explicit light or dark (opposite of current effective appearance). */
export function flipThemeLightDark(): void {
	if (!brandColorsDark) return;
	setThemePreference(computeEffectiveDark() ? "light" : "dark");
}

// ---------------------------------------------------------------------------
// Dark mode detection (OS)
// ---------------------------------------------------------------------------

if (typeof window !== "undefined" && window.matchMedia) {
	const mq = window.matchMedia("(prefers-color-scheme: dark)");
	prefersDark = mq.matches;
	mq.addEventListener("change", (e) => {
		prefersDark = e.matches;
		refreshRootThemeAttribute();
		bumpThemeEpoch();
	});
}

// ---------------------------------------------------------------------------
// Kept for backwards compat -- BrandKey is now just a display hint
// ---------------------------------------------------------------------------

export type BrandKey = string;

// ---------------------------------------------------------------------------
// Initialization (called once from index.tsx after tenant resolution)
// ---------------------------------------------------------------------------

function buildPalette(primary: string, accent: string): BrandColorSet {
	const p = normalizeHex(primary);
	const a = normalizeHex(accent);
	return {
		primary: p,
		accent: a,
		currentStep: p,
		nextStep: a,
		footerBtn: lightenHex(a, 0.35),
		footerBtnDisabled: a,
		genderToggleBtn: a,
		physicalKey: p,
		physicalKeyText: "#FFFFFF",
		guestPlaceholderBanner: hexToRgba(a, 0.15),
		lightAccent: lightenHex(p, 0.4),
	};
}

/**
 * Applies branding from the chain-info API response. Call this once after
 * {@link initializeTenant} completes. All subsequent calls to
 * `getBrandColors()`, `getBrandLogo()`, `getBrandText()` will reflect
 * these values.
 */
export function applyBranding(branding: ChainBranding, chainName: string): void {
	lastChainBranding = branding;

	const primary = normalizeHex(branding.primaryColor || DEFAULT_PRIMARY);
	const accent = normalizeHex(branding.accentColor || DEFAULT_ACCENT);

	brandColorsLight = buildPalette(primary, accent);

	const darkAllowed = branding.darkBrandingEnabled === true;
	const primaryDark = branding.primaryColorDark?.trim();
	const accentDark = branding.accentColorDark?.trim();
	if (darkAllowed && primaryDark && accentDark) {
		brandColorsDark = buildPalette(normalizeHex(primaryDark), normalizeHex(accentDark));
	} else {
		brandColorsDark = null;
	}

	userThemePreference = loadThemePreferenceFromStorage();
	if (!brandColorsDark && userThemePreference !== "system") {
		persistThemePreference("system");
	}

	brandLogoUrl = branding.logoUrl ?? "";
	brandLogoDarkUrl = branding.logoDarkUrl ?? "";
	brandTexts = {
		newsletterCheckboxBrand: branding.newsletterBrand || chainName || "",
	};
	refreshRootThemeAttribute();
	bumpThemeEpoch();
}

function refreshRootThemeAttribute(): void {
	if (typeof document === "undefined") return;
	const useDarkUi = computeEffectiveDark();
	document.documentElement.setAttribute("data-oc-theme", useDarkUi ? "dark" : "light");
	syncThemeCssVariables();
}

/**
 * Surfaces + text colors for the app shell and MUI fields. Toggle when light/dark changes.
 * Optional {@link ChainBranding.backgroundColorLight} / {@link ChainBranding.backgroundColorDark} override defaults.
 */
function syncThemeCssVariables(): void {
	if (typeof document === "undefined") return;
	const root = document.documentElement;
	const dark = computeEffectiveDark() && brandColorsDark !== null;

	const b = lastChainBranding;
	const pageLight = b?.backgroundColorLight?.trim() || "#FAF9F6";
	const pageDark = b?.backgroundColorDark?.trim() || "#12121a";
	const paperLight = "#ffffff";
	const paperDark = "#1a1a28";
	const textLight = "#1a1a1a";
	const textDark = "#ececf1";
	const mutedLight = "#666666";
	const mutedDark = "#a4a4b0";
	const borderLight = "rgba(0, 0, 0, 0.14)";
	const borderDark = "rgba(255, 255, 255, 0.14)";
	const footerLight = "#ede8e3";
	const footerDark = "#252532";

	if (dark) {
		root.style.setProperty("--oc-page-bg", pageDark);
		root.style.setProperty("--oc-paper-bg", paperDark);
		root.style.setProperty("--oc-text", textDark);
		root.style.setProperty("--oc-text-muted", mutedDark);
		root.style.setProperty("--oc-border", borderDark);
		root.style.setProperty("--oc-footer-bg", footerDark);
		root.style.setProperty("--oc-navbar-bg", "#16161f");
	} else {
		root.style.setProperty("--oc-page-bg", pageLight);
		root.style.setProperty("--oc-paper-bg", paperLight);
		root.style.setProperty("--oc-text", textLight);
		root.style.setProperty("--oc-text-muted", mutedLight);
		root.style.setProperty("--oc-border", borderLight);
		root.style.setProperty("--oc-footer-bg", footerLight);
		root.style.setProperty("--oc-navbar-bg", "#ffffff");
	}
}

// ---------------------------------------------------------------------------
// Legacy setBrand / getCurrentBrand kept for any code that still references
// them; they now no-op or return a generic key.
// ---------------------------------------------------------------------------

let currentBrandKey: BrandKey = "default";

/** @deprecated Use {@link applyBranding} instead. */
export const setBrand = (_brand: BrandKey) => {
	currentBrandKey = _brand;
};

export const getCurrentBrand = (): BrandKey => currentBrandKey;

// ---------------------------------------------------------------------------
// Public accessors (unchanged signatures -- components keep working)
// ---------------------------------------------------------------------------

/**
 * Returns the active brand color set. Automatically switches between
 * light and dark palettes based on `prefers-color-scheme` if a dark
 * palette was configured.
 */
export const getBrandColors = (): BrandColorSet => {
	if (computeEffectiveDark() && brandColorsDark) {
		return brandColorsDark;
	}
	return brandColorsLight;
};

export const isDarkMode = (): boolean => computeEffectiveDark();

export const getBrandLogo = (variant: "light" | "dark" = "dark"): string => {
	if (variant === "light") {
		return brandLogoUrl;
	}
	const dark = brandLogoDarkUrl;
	if (dark) return dark;
	return brandLogoUrl;
};

export const getBrandText = (): BrandTexts => brandTexts;

// ---------------------------------------------------------------------------
// Colour helpers (no external dependencies)
// ---------------------------------------------------------------------------

function hexToRgba(hex: string, alpha: number): string {
	const h = normalizeHex(hex).replace("#", "");
	if (h.length !== 6) return `rgba(124, 98, 75, ${alpha})`;
	const r = parseInt(h.substring(0, 2), 16);
	const g = parseInt(h.substring(2, 4), 16);
	const b = parseInt(h.substring(4, 6), 16);
	if (isNaN(r) || isNaN(g) || isNaN(b)) return `rgba(124, 98, 75, ${alpha})`;
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function lightenHex(hex: string, amount: number): string {
	const full = normalizeHex(hex).replace("#", "");
	if (full.length !== 6) return hex;
	let r = parseInt(full.substring(0, 2), 16);
	let g = parseInt(full.substring(2, 4), 16);
	let b = parseInt(full.substring(4, 6), 16);
	if (isNaN(r) || isNaN(g) || isNaN(b)) return `#${full}`;
	r = Math.min(255, Math.round(r + (255 - r) * amount));
	g = Math.min(255, Math.round(g + (255 - g) * amount));
	b = Math.min(255, Math.round(b + (255 - b) * amount));
	return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

/** Ensures #RRGGBB; supports #RGB; falls back to default primary on garbage input. */
function normalizeHex(hex: string): string {
	const s = hex.trim();
	if (!s) return DEFAULT_PRIMARY;
	const raw = s.startsWith("#") ? s.slice(1) : s;
	if (/^[0-9a-fA-F]{3}$/.test(raw)) {
		const a = raw[0];
		const b = raw[1];
		const c = raw[2];
		return `#${a}${a}${b}${b}${c}${c}`.toLowerCase();
	}
	if (/^[0-9a-fA-F]{6}$/.test(raw)) {
		return `#${raw.toLowerCase()}`;
	}
	return DEFAULT_PRIMARY;
}
